import React from "react"
import { observer } from "mobx-react"
import { useRouter } from "next/router"
import { useTranslations } from "next-intl"
import styled from "styled-components"
import useInject from "~/hooks/useInject"
import Spacer from "~/components/Spacer"
import ToggleSwitch from "./Forms/ToggleSwitch"
import DesktopContainer from "~/components/DesktopContainer"
import WhatWeOffer from "~/components/WhatWeOffer"
import { PARKING_SEARCH_TYPE, PARKING_FLOW_TYPE } from "~/utilities/constants"
import Grid from "~/components/parking/Landing/Forms/Grid"
import LoadingModal from "./LoadingModal"
import SearchFormContainer from "~/components/parking/Landing/Forms/SearchFormContainer"

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const PayNYSTrafficText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextBold};
  color: ${(props) => props.theme.colors.menuItem};
  font-size: ${(props) => (props.isMobile ? "1.875rem" : "2.1875rem")};
  font-weight: bold;
`
const MostSecureText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => props.theme.colors.text.subContent};
  font-size: 1.25rem;
`

const WhatWeOfferText = styled.div`
  display: flex;
  justify-content: center;
  direction: row;
  font-family: ${(props) => props.theme.fonts.avenirNextBold};
  color: ${(props) => props.theme.colors.menuItem};
  font-size: ${(props) => (props.isMobile ? "1.5625rem" : "1.875rem")};
  font-weight: bold;
`
const ToggleSwitchContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  height: 54px;
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
`

const GradientBackground = styled.div`
  display: flex;
  flex-grow: 1;
  background-repeat: no-repeat;
  background-position: right bottom;
  background: linear-gradient(
    180deg,
    ${(props) => props.theme.colors.background.main} 13%,
    ${(props) => props.theme.colors.background.page} 100%
  );
`

const DisclaimerText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => props.theme.colors.text.subContent};
  font-size: 1.1rem;
`

const mapStore = (store) => ({
  isMobile: store.ui.isMobile,
  entryType: store.parkingFlow.flow.entryType,
  activeSearchType: store.parkingFlow.searchCriteria.type,
  changeSearchType: store.parkingFlow.searchCriteria.changeSearchType,
  currentHost: store.ui.currentHost,
})

const ParkingFlowLanding = () => {
  const {
    isMobile,
    entryType,
    activeSearchType,
    changeSearchType,
    currentHost,
  } = useInject(mapStore)
  const translate = useTranslations()
  const router = useRouter()

  let headerText
  let subText
  switch (entryType) {
    default:
    case PARKING_FLOW_TYPE.both:
      headerText = translate("parking.headerText.bothType")
      subText = translate("parking.subText.bothType")
      break
    case PARKING_FLOW_TYPE.pay:
      headerText = translate("parking.headerText.payType")
      subText = translate("parking.subText.payType")
      break
    case PARKING_FLOW_TYPE.fight:
      headerText = translate("parking.headerText.fightType")
      subText = translate("parking.subText.fightType")
      break
  }
  return (
    <>
      <h1 style={{ display: "none" }}>ParkingFlowLanding</h1>
      <LoadingModal />
      <PageContainer>
        <GradientBackground tower>
          <DesktopContainer>
            <Spacer mobile={16} desktop={16} />
            <PayNYSTrafficText isMobile={isMobile}>
              {headerText}
            </PayNYSTrafficText>
            <Spacer mobile={29} desktop={34} />
            <MostSecureText isMobile={isMobile}>{subText}</MostSecureText>
            <Spacer mobile={16} desktop={40} />
            <ToggleSwitchContainer>
              <ToggleSwitch
                handleSwitch={changeSearchType}
                active={activeSearchType}
                options={[
                  {
                    name: translate("parking.searchTypes.plate"),
                    value: PARKING_SEARCH_TYPE.plate,
                  },
                  {
                    name: translate("parking.searchTypes.ticket"),
                    value: PARKING_SEARCH_TYPE.ticketId,
                  },
                ]}
              />
            </ToggleSwitchContainer>
            <Spacer mobile={10} desktop={10} />
            <FormContainer>
              <Grid>
                <SearchFormContainer
                  queryPlateNumber={router.query.plate}
                  queryPlateState={router.query.state}
                />
              </Grid>
            </FormContainer>
          </DesktopContainer>
        </GradientBackground>
        <Spacer mobile={25} desktop={50} />
        <DesktopContainer>
          <Spacer mobile={20} desktop={90} />
          <WhatWeOfferText>
            {translate("parking.heroTexts.offerings.heading")}
          </WhatWeOfferText>
          <Spacer mobile={20} desktop={110} />
          <WhatWeOffer isMobile={isMobile} />
          <Spacer mobile={20} desktop={100} />
          {(currentHost && currentHost.includes("paynyctickets.com")) || entryType === PARKING_FLOW_TYPE.pay ? (
            <DisclaimerText>
              <b>*</b>
              WinIt is a privately held company which charges the fees for this service that are listed on the payments page. It is not affiliated, associated, authorized, endorsed by, or in any way officially connected with any city, county or state government agency. WinIt charges the fees for the services that are listed on the payments page. Alternative low or no fee options may be available.
            </DisclaimerText>
          ) : null}
          <Spacer mobile={20} desktop={100} />
        </DesktopContainer>
      </PageContainer>
    </>
  )
}

export default observer(ParkingFlowLanding)
